import React, { useState } from 'react';
import { useCustomerTheme } from '../Theme';

import { useTranslation } from 'react-i18next';

import { Forward, Home, Layers, Presentation, X, type LucideIcon } from 'lucide-react';

import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '@/components/ui/tooltip';
import { chipResourceKey } from './chipResourceKey';
import { useResolvedResourceContent } from './useResolvedResourceContent';
interface SuggestionAction {
    type: string;
    payload: {
        suggestionId: string;
    };
}

interface SuggestionMetadata {
    domain?: 'location_intel' | 'property_private' | 'rfp_assessment' | 'sales_material' | 'kyc';
    expectedEffort?: 'low' | 'medium' | 'high';
    requiresUserInput?: boolean;
    missingInputs?: string[];
}

interface Suggestion {
    id: string;
    text: string;
    label?: string;
    action: SuggestionAction;
    metadata?: SuggestionMetadata;
}

interface FollowUpSuggestionsContent {
    language: string;
    style: string;
    suggestions: Suggestion[];
}

interface FollowUpSuggestionsResourceProps {
    resource: {
        type: 'followUpSuggestions';
        /** Im Stream steht statt des Inhalts nur diese Referenz. */
        reference?: string;
        data?: FollowUpSuggestionsContent;
        content?: FollowUpSuggestionsContent;
    };
    onSubmitInput?: (input: string, prompt?: string, options?: { onAccepted?: () => void }) => void;
    isLastMessage?: boolean;
    stableKey?: string;
    pendingRequest?: boolean;
}

/**
 * Symbol je Fachbereich. `rfp_assessment` und `kyc` bleiben bewusst ohne Symbol:
 * ihre Icon-Namen aus dem Bestand ("Checklist", "ContactInfo") hatten in der
 * Icon-Registry keine Entsprechung und wurden schon vorher nicht gezeichnet.
 */
const getIconForDomain = (domain?: string): LucideIcon | null => {
    switch (domain) {
        case 'location_intel': return Layers;
        case 'property_private': return Home;
        case 'rfp_assessment': return null;
        case 'sales_material': return Presentation;
        case 'kyc': return null;
        default: return Forward;
    }
};

// Global state for clicked suggestions to persist across re-renders
const clickedSuggestions = new Set<string>();

export const FollowUpSuggestionsResource: React.FC<FollowUpSuggestionsResourceProps> = ({ resource, onSubmitInput, isLastMessage, stableKey, pendingRequest }) => {
    const { theme } = useCustomerTheme();
    const { t } = useTranslation();

    // Support both 'data' (TRI legacy) and 'content' (MCP style)
    // Im Stream kommt nur eine Referenz an — der Inhalt wird nachgeladen.
    const content = useResolvedResourceContent<FollowUpSuggestionsContent>(resource);
    const suggestions = content?.suggestions || [];

    // Use stableKey if provided, otherwise fallback to legacy persistence key
    const resourceKey = chipResourceKey(stableKey, 'followUpSuggestions', suggestions.map((suggestion: any) => suggestion.id));

    const [hasClicked, setHasClicked] = useState(() => clickedSuggestions.has(resourceKey));

    // Only show if it's the last message and hasn't been clicked yet
    if (!isLastMessage || hasClicked || suggestions.length === 0) return null;

    const handleSuggestionClick = (suggestion: Suggestion) => {
        // Don't process clicks while a request is pending to avoid hiding the suggestions
        // before submitInput can actually process the request.
        if (pendingRequest || !onSubmitInput) {
            return;
        }
        // Use the label for the UI (bubble) and text for the assistant.
        // Only mark consumed once submitInput accepts the request — otherwise a
        // still-pending prior turn makes submitInput return immediately and the
        // suggestions would stay hidden forever via module-level state.
        const displayLabel = suggestion.label || suggestion.text;
        onSubmitInput(`Ja, bitte: ${displayLabel}`, suggestion.text, {
            onAccepted: () => {
                setHasClicked(true);
                clickedSuggestions.add(resourceKey);
            },
        });
    };

    return (
        <div className="follow-up-suggestions mb-2 mt-3">
            {/* Header line as requested */}
            <div className="mb-2 text-xs font-bold uppercase tracking-[0.5px] text-muted-foreground">
                {t('chatBotPage.nextSteps', 'Nächste Schritte')}
            </div>

            <TooltipProvider delayDuration={300} skipDelayDuration={0}>
                <div className="flex flex-wrap items-center gap-2">
                    {suggestions.map((suggestion) => {
                        const DomainIcon = getIconForDomain(suggestion.metadata?.domain);

                        return (
                            <Tooltip key={suggestion.id}>
                                <TooltipTrigger asChild>
                                    <button
                                        type="button"
                                        data-testid="chat-resource-followup-suggestion"
                                        onClick={() => handleSuggestionClick(suggestion)}
                                        // Die Kundenfarbe kommt zur Laufzeit aus dem Theme — als CSS-Variable
                                        // gesetzt, damit der Hover-Zustand ohne JS-Stilmutation auskommt.
                                        style={{
                                            '--chip-border': `${theme.primaryLight}44`,
                                            '--chip-border-hover': theme.primaryLight,
                                        } as React.CSSProperties}
                                        className="flex max-w-[300px] items-center rounded-[20px] border border-[color:var(--chip-border)] bg-muted px-[14px] py-1.5 text-left text-foreground transition-all duration-200 hover:border-[color:var(--chip-border-hover)] hover:bg-accent hover:shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
                                    >
                                        {DomainIcon && (
                                            <DomainIcon
                                                aria-hidden
                                                className="mr-2 h-3.5 w-3.5 shrink-0"
                                                style={{ color: theme.primaryLight }}
                                            />
                                        )}
                                        <span className="line-clamp-2 text-[13px] leading-[1.4]">
                                            {suggestion.label || suggestion.text}
                                        </span>
                                    </button>
                                </TooltipTrigger>
                                <TooltipContent sideOffset={0}>{suggestion.text}</TooltipContent>
                            </Tooltip>
                        );
                    })}

                    {/* Tertiary dismiss button as requested */}
                    <button
                        type="button"
                        data-testid="chat-resource-followup-dismiss"
                        className="flex items-center gap-1 rounded-md px-2 py-1 text-xs text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        onClick={() => {
                            setHasClicked(true);
                            clickedSuggestions.add(resourceKey);
                        }}
                    >
                        <X aria-hidden className="h-2.5 w-2.5" />
                        {t('global.noThanks', 'Nein danke')}
                    </button>
                </div>
            </TooltipProvider>
        </div>
    );
};
